import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import XeroContactLinkingSection from './xero/XeroContactLinkingSection'
import GoCardlessMandateSection from './GoCardlessMandateSection'
import { updateCustomer } from '@/supabase-client'
import { supabase } from '@/supabase-client'
import { CreditCard, Building2, Banknote, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

/**
 * Payment Integration Modal Component
 *
 * Comprehensive payment setup workflow for new customers:
 * 1. Payment method selection (Cash, Xero, GoCardless)
 * 2. Integration setup based on selected method:
 *    - Cash: Create Xero contacts with reminders OFF
 *    - Xero: Link/create Xero contact per branch with reminders ON
 *    - GoCardless: Link mandate + create Xero contacts with reminders OFF
 * 3. Email validation based on payment method requirements
 *
 * Business Rules:
 * - All payment methods create Xero contacts
 * - Cash: Reminders OFF, email optional
 * - Xero: Reminders ON, email required
 * - GoCardless: Reminders OFF (handled by DD), email required
 * - One Xero contact per customer (shared across all branches)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.customer - Customer object with branches
 * @param {Function} props.onIntegrationComplete - Callback after successful setup
 */
export default function PaymentIntegrationModal({ isOpen, onClose, customer, onIntegrationComplete }) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null) // 'cash' | 'xero' | 'gocardless' | null
  const [currentStep, setCurrentStep] = useState('select') // 'select' | 'setup' | 'confirm'
  const [customerBranches, setCustomerBranches] = useState([])
  const [customerXeroMapping, setCustomerXeroMapping] = useState({ xeroContactId: null, xeroContactName: null })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [emailValidationError, setEmailValidationError] = useState(null)

  // Fetch customer data with Xero mapping when modal opens
  useEffect(() => {
    if (isOpen && customer?.id) {
      loadCustomerData()
    }
  }, [isOpen, customer?.id])

  const loadCustomerData = async () => {
    setIsLoading(true)
    try {
      // Fetch customer's Xero mapping
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('xero_contact_id, xero_contact_name')
        .eq('id', customer.id)
        .single()

      if (customerError) throw customerError

      setCustomerXeroMapping({
        xeroContactId: customerData?.xero_contact_id || null,
        xeroContactName: customerData?.xero_contact_name || null
      })

      // Fetch branches for reference display
      const { data: branchData, error: branchError } = await supabase
        .from('branches')
        .select('id, name, address')
        .eq('customer_id', customer.id)
        .order('name')

      if (branchError) throw branchError

      setCustomerBranches(branchData || [])
    } catch (error) {
      console.error('[PaymentIntegrationModal] Error loading customer data:', error)
      toast.error('Failed to load customer data')
    } finally {
      setIsLoading(false)
    }
  }

  // Validate email requirements based on payment method
  const validateEmailRequirement = () => {
    setEmailValidationError(null)

    if (!selectedPaymentMethod) return true

    // Cash: Email optional
    if (selectedPaymentMethod === 'cash') return true

    // Xero and GoCardless: Email required
    if (!customer?.email || !customer.email.trim()) {
      setEmailValidationError(
        selectedPaymentMethod === 'xero'
          ? 'Email address is required for Xero invoicing'
          : 'Email address is required for GoCardless direct debit'
      )
      return false
    }

    return true
  }

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method)
    setCurrentStep('setup')

    // Validate email requirement
    validateEmailRequirement()
  }

  const handleCustomerMappingChange = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('xero_contact_id, xero_contact_name')
        .eq('id', customer.id)
        .single()

      if (error) throw error

      setCustomerXeroMapping({
        xeroContactId: data?.xero_contact_id || null,
        xeroContactName: data?.xero_contact_name || null
      })
    } catch (error) {
      console.error('[PaymentIntegrationModal] Error refreshing customer mapping:', error)
      loadCustomerData()
    }
  }

  const handleConfirmSetup = async () => {
    // Validate email requirement with specific guidance
    if (!validateEmailRequirement()) {
      const emailError = selectedPaymentMethod === 'xero'
        ? 'Email address is required to send Xero invoices with payment links. Please add an email address to this customer.'
        : 'Email address is required for GoCardless direct debit setup. Please add an email address to this customer.'
      toast.error(emailError, { duration: 5000 })
      return
    }

    // Check if customer has Xero contact linked for Xero/Cash payment methods
    if (selectedPaymentMethod !== 'gocardless') {
      if (!customerXeroMapping.xeroContactId) {
        toast.error('Please link a Xero contact for this customer before saving.', { duration: 6000 })
        return
      }
    }

    setIsSaving(true)
    try {
      // Update customer with payment method
      const updated = await updateCustomer(customer.id, {
        paymentMethod: selectedPaymentMethod
      })

      const successMsg = selectedPaymentMethod === 'cash'
        ? 'Cash payment method configured. Invoices will be tracked in Xero with reminders disabled.'
        : selectedPaymentMethod === 'xero'
        ? 'Xero payment method configured. Invoices will be sent with online payment links and automatic reminders.'
        : 'GoCardless payment method configured. Payments will be collected automatically via direct debit.'

      toast.success(successMsg, { duration: 4000 })

      // Call parent callback with updated customer
      if (onIntegrationComplete) {
        onIntegrationComplete({
          ...customer,
          payment_method: selectedPaymentMethod,
          branches: customerBranches
        })
      }

      // Close modal after successful save
      onClose()
    } catch (error) {
      console.error('[PaymentIntegrationModal] Failed to save payment method:', error)
      const errorDetails = error.message || 'Unknown error occurred'
      toast.error(`Failed to save payment method: ${errorDetails}. Please try again or contact support.`, { duration: 6000 })
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    if (currentStep === 'setup') {
      setCurrentStep('select')
      setSelectedPaymentMethod(null)
      setEmailValidationError(null)
    }
  }

  const handleSkip = () => {
    onClose()
  }

  // Render payment method selection cards
  const renderPaymentMethodSelection = () => (
    <div className="space-y-4">
      {/* Cash Card */}
      <Card
        className="cursor-pointer hover:border-[hsl(var(--odin-green))] transition-colors"
        onClick={() => handlePaymentMethodSelect('cash')}
      >
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
              <Banknote className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <CardTitle>Cash Payment</CardTitle>
              <CardDescription>
                Manual payments - Xero contact created, reminders disabled
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Invoice tracking only. Payment reminders OFF. Email optional.
          </p>
          <Button className="w-full" variant="outline">
            Set Up Cash
          </Button>
        </CardContent>
      </Card>

      {/* Xero Card */}
      <Card
        className="cursor-pointer hover:border-[hsl(var(--odin-green))] transition-colors"
        onClick={() => handlePaymentMethodSelect('xero')}
      >
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Xero Online Payment</CardTitle>
              <CardDescription>
                Online payments - Email required, automatic reminders enabled
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Automated invoicing with payment reminders ON. Email required.
          </p>
          <Button className="w-full" variant="outline">
            Set Up Xero
          </Button>
        </CardContent>
      </Card>

      {/* GoCardless Card */}
      <Card
        className="cursor-pointer hover:border-[hsl(var(--odin-green))] transition-colors"
        onClick={() => handlePaymentMethodSelect('gocardless')}
      >
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <CardTitle>GoCardless Direct Debit</CardTitle>
              <CardDescription>
                Direct debit - Single mandate for all branches
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Automated collection via direct debit. Email required. Reminders OFF.
          </p>
          <Button className="w-full" variant="outline">
            Set Up GoCardless
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end pt-4 border-t">
        <Button variant="ghost" onClick={handleSkip}>
          Skip for Now
        </Button>
      </div>
    </div>
  )

  // Render customer-level Xero linking for Cash/Xero payment methods
  const renderXeroCustomerSetup = () => {
    if (!customer) return null
    return (
    <>
      {emailValidationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{emailValidationError}</AlertDescription>
        </Alert>
      )}

      <div className="text-sm text-muted-foreground">
        {selectedPaymentMethod === 'cash'
          ? 'Create a Xero contact for this customer (payment reminders will be disabled)'
          : 'Link or create a Xero contact for this customer (payment reminders will be enabled)'
        }
      </div>

      <div className="space-y-4">
        <div className="font-medium flex items-center gap-2">
          Xero Contact
          {customerXeroMapping.xeroContactId && (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          )}
        </div>
        <XeroContactLinkingSection
          xeroContactId={customerXeroMapping.xeroContactId}
          xeroContactName={customerXeroMapping.xeroContactName}
          odinCustomer={{
            id: customer.id,
            name: customer.name || customer.companyName,
            email: customer.email,
            phone: customer.phone
          }}
          onMappingChange={handleCustomerMappingChange}
        />
      </div>

      {/* Show branches for reference */}
      {customerBranches.length > 1 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm font-medium mb-2">Branches to be invoiced:</div>
          <div className="text-sm text-muted-foreground space-y-1">
            {customerBranches.map((branch, index) => (
              <div key={branch.id}>• {branch.name || `Branch ${index + 1}`}</div>
            ))}
          </div>
        </div>
      )}
    </>
  )}

  // Render GoCardless mandate setup
  const renderGoCardlessSetup = () => (
    <>
      {emailValidationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{emailValidationError}</AlertDescription>
        </Alert>
      )}

      <div className="text-sm text-muted-foreground">
        Link to an existing GoCardless mandate. This mandate will be used for all branches.
        A Xero contact will be created for the customer with payment reminders disabled.
      </div>

      <GoCardlessMandateSection
        odinCustomer={customer}
        onLinkSuccess={() => {
          // After GoCardless linking, still need to create Xero contacts
          setCurrentStep('confirm')
        }}
      />

      {/* Show branch list for reference */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium mb-2">Branches to be invoiced:</div>
        <div className="text-sm text-muted-foreground space-y-1">
          {customerBranches.map((branch, index) => (
            <div key={branch.id}>• {branch.name || `Branch ${index + 1}`}</div>
          ))}
        </div>
      </div>
    </>
  )

  // Render footer buttons for setup steps
  const renderFooterButtons = () => (
    <div className="flex justify-between">
      <Button variant="ghost" onClick={handleBack}>
        Back
      </Button>
      <Button
        onClick={handleConfirmSetup}
        disabled={isSaving || emailValidationError}
      >
        {isSaving ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          'Confirm Setup'
        )}
      </Button>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col overflow-hidden">
        {/* Fixed Header */}
        <DialogHeader className="shrink-0 pb-4">
          <DialogTitle>
            {currentStep === 'select'
              ? 'Set Up Payment Method'
              : `Set Up ${
                  selectedPaymentMethod === 'cash' ? 'Cash Payment' :
                  selectedPaymentMethod === 'xero' ? 'Xero Integration' :
                  'GoCardless Direct Debit'
                }`
            }
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'select'
              ? `Choose how ${customer?.name || customer?.companyName || 'this customer'} will pay for invoices`
              : `Configure ${selectedPaymentMethod} payment for ${customer?.name || customer?.companyName || 'this customer'}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : currentStep === 'select' ? (
            renderPaymentMethodSelection()
          ) : (
            <div className="space-y-4">
              {selectedPaymentMethod === 'gocardless' ? (
                renderGoCardlessSetup()
              ) : (
                renderXeroCustomerSetup()
              )}
            </div>
          )}
        </div>

        {/* Fixed Footer (only for setup steps) */}
        {currentStep !== 'select' && !isLoading && (
          <div className="shrink-0 border-t pt-4 mt-4 bg-white">
            {renderFooterButtons()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}